import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../ui/Button.js';
import { useTenantCode } from '../tenant/TenantProvider.js';
import { isPlatformChromeTenant } from '../../app/platformChromeTenant.js';
import { resolveHierarchicalBackTarget } from '../../navigation/hierarchicalBack.js';
import {
  resolveCustomerShopFallbackPath,
  resolveCustomerSignInPath,
  resolveCustomerSignUpPath,
} from '../../features/auth/customerAuthPaths.js';
import {
  inferPaymentResumeReturnSource,
  isPaymentResumeReturnPath,
} from '../session/isPaymentResumeReturnPath.js';
import {
  parkPaymentResumeReturnTarget,
  saveReturnTarget,
  stripOidcLandQueryParam,
} from '../session/returnTarget.js';
import type { RecoveryCtaKind, RecoveryDefinition, RecoveryQueryContext } from './types.js';

export interface RecoverySurfaceProps {
  readonly definition: RecoveryDefinition;
  readonly context?: RecoveryQueryContext;
  /** Side-effect only (e.g. analytics) — does not replace navigation. */
  readonly onPrimaryAction?: () => void;
  /** Side-effect only (e.g. analytics) — does not replace navigation. */
  readonly onSecondaryAction?: () => void;
}

function ctaLabelKey(prefix: string, kind: RecoveryCtaKind): string {
  return `${prefix}.cta.${kind}`;
}

function RecoveryLockIcon(): JSX.Element {
  return (
    <svg
      width="40"
      height="40"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className="text-[var(--color-accent)]"
    >
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </svg>
  );
}

/**
 * Platform chrome has no tenant shop — remap shop-like escapes to org picker
 * so CTAs do not loop `/__platform__/shop` → access-denied.
 */
function resolvePlatformSafeCta(
  kind: RecoveryCtaKind,
  platformChrome: boolean,
): RecoveryCtaKind {
  if (
    platformChrome &&
    (kind === 'shop' || kind === 'dismiss' || kind === 'cancel')
  ) {
    return 'tenant-select';
  }
  return kind;
}

function resolveEffectiveSecondaryCta(
  definition: RecoveryDefinition,
  platformChrome: boolean,
): RecoveryCtaKind | undefined {
  const secondary = definition.secondaryCta;
  if (secondary === undefined) {
    return undefined;
  }
  return resolvePlatformSafeCta(secondary, platformChrome);
}

export function RecoverySurface({
  definition,
  context,
  onPrimaryAction,
  onSecondaryAction,
}: RecoverySurfaceProps): JSX.Element {
  const { t } = useTranslation('customer');
  const navigate = useNavigate();
  const location = useLocation();
  const tenantCode = useTenantCode();
  const platformChrome = isPlatformChromeTenant(tenantCode);
  const prefix = definition.i18nKeyPrefix;
  const shopPath = resolveCustomerShopFallbackPath(tenantCode);
  const primaryCta = useMemo(
    () => resolvePlatformSafeCta(definition.primaryCta, platformChrome),
    [definition.primaryCta, platformChrome],
  );
  const secondaryCta = useMemo(
    () => resolveEffectiveSecondaryCta(definition, platformChrome),
    [definition, platformChrome],
  );

  const navigateCta = useCallback(
    (kind: RecoveryCtaKind): void => {
      switch (kind) {
        case 'sign-in': {
          const returnTo = context?.returnTo;
          // Persist (strips oidcLand). No unused ?returnTarget= URL.
          // G1 SoftRace — elevate only checkout/donate resume parks
          // (isPaymentResumeReturnPath excludes /account/** hubs).
          // Payment elevate: navigate only after park succeeds (non-null).
          if (returnTo !== undefined && returnTo.length > 0) {
            const paymentSource = inferPaymentResumeReturnSource(returnTo);
            if (paymentSource !== null && isPaymentResumeReturnPath(returnTo)) {
              const parked = parkPaymentResumeReturnTarget(returnTo, {
                tenantCode,
                source: paymentSource,
              });
              if (parked === null) {
                return;
              }
            } else {
              saveReturnTarget(returnTo, { tenantCode, source: 'account' });
            }
          }
          navigate(resolveCustomerSignInPath(tenantCode));
          break;
        }
        case 'register':
          navigate(resolveCustomerSignUpPath(tenantCode));
          break;
        case 'shop':
          navigate(shopPath);
          break;
        case 'shops':
          // SSOT: resolveCustomerShopFallbackPath → resolveGuestShopHomeHref
          // (empty guest → /browse; listable favs → /favorites). Never hardcode /shops.
          navigate(shopPath);
          break;
        case 'tenant-select':
          navigate('/');
          break;
        case 'retry':
        case 'resume':
          if (context?.returnTo !== undefined && context.returnTo.length > 0) {
            // Same H1 strip as saveReturnTarget — resume must not re-seed oidcLand.
            navigate(stripOidcLandQueryParam(context.returnTo));
          } else {
            window.location.reload();
          }
          break;
        case 'reload':
          window.location.reload();
          break;
        case 'back': {
          // Named parent only — never navigate(-1) / history.back() on cold start.
          const hierarchical = resolveHierarchicalBackTarget(
            location.pathname,
            tenantCode,
            { search: location.search },
          );
          navigate(hierarchical?.parentPath ?? shopPath);
          break;
        }
        case 'dismiss':
        case 'cancel':
          navigate(shopPath);
          break;
        case 'support':
          if (platformChrome) {
            navigate('/');
            break;
          }
          navigate('/account/account-and-privacy');
          break;
        default:
          navigate(shopPath);
      }
    },
    [context, location.pathname, location.search, navigate, platformChrome, shopPath, tenantCode],
  );

  const runCta = useCallback(
    (kind: RecoveryCtaKind, sideEffect?: () => void): void => {
      if (sideEffect !== undefined) {
        sideEffect();
      }
      navigateCta(kind);
    },
    [navigateCta],
  );

  const isStub = definition.presentation === 'stub';
  const showLockIcon = definition.classSlug === 'access-denied';
  const descriptionKey =
    showLockIcon && platformChrome
      ? `${prefix}.descriptionPlatform`
      : `${prefix}.description`;

  return (
    <section
      className="mx-auto flex w-full min-w-0 max-w-md flex-col gap-8 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface-elevated)] p-6 shadow-[var(--shadow-card)] sm:gap-10 sm:p-8"
      data-testid={`recovery-surface-${definition.classSlug}`}
      data-ms-id={definition.msId}
    >
      <div className="flex min-w-0 flex-col gap-4">
        {showLockIcon ? (
          <div
            className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-[var(--color-surface-muted)]"
            aria-hidden
          >
            <RecoveryLockIcon />
          </div>
        ) : null}
        <div className="flex min-w-0 flex-col gap-3">
          <h1 className="text-balance break-words text-2xl font-semibold tracking-tight text-[var(--color-on-surface)] sm:text-3xl">
            {t(`${prefix}.title`)}
          </h1>
          <p className="text-pretty break-words text-base leading-relaxed text-[var(--color-on-surface-muted)] sm:text-lg">
            {isStub ? t('retail.rec.stub.description') : t(descriptionKey)}
          </p>
          {context?.paymentId !== undefined ? (
            <p
              className="break-all text-sm text-[var(--color-on-surface-muted)]"
              data-testid="recovery-context-payment-id"
            >
              {t('retail.rec.context.paymentId', { paymentId: context.paymentId })}
            </p>
          ) : null}
        </div>
      </div>

      <div className="flex w-full min-w-0 flex-col gap-3">
        <Button
          type="button"
          intent="primary"
          size="lg"
          block
          className="max-w-none whitespace-nowrap"
          data-testid="recovery-primary-cta"
          onClick={() => {
            runCta(primaryCta, onPrimaryAction);
          }}
        >
          {t(ctaLabelKey(prefix, primaryCta))}
        </Button>
        {secondaryCta !== undefined ? (
          <Button
            type="button"
            intent="secondary"
            size="lg"
            block
            className="max-w-none whitespace-nowrap"
            data-testid="recovery-secondary-cta"
            onClick={() => {
              runCta(secondaryCta, onSecondaryAction);
            }}
          >
            {t(ctaLabelKey(prefix, secondaryCta))}
          </Button>
        ) : null}
      </div>
    </section>
  );
}
